import { cloneElement, ReactElement } from 'react';

import styled from 'styled-components';

import { IconProps } from '@components/icons';
import { IconSearch } from '@components/icons/search';
import { Link } from '@components/link';
import { Stack } from '@components/stack';
import { Color, Spacing, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { Text } from '../text';

export interface EmptyStateProps {
  variant?: 'base' | 'small';
  icon: ReactElement;
  iconShadow?: ReactElement;
  header?: string;
  content: string | JSX.Element;
  headerColor?: Color;
  contentColor?: Color;
  orientation?: 'horizontal' | 'vertical';
  gap?: Spacing;
  transparent?: boolean;
  skipMargin?: boolean;
  skipPadding?: boolean;
  action?: ReactElement;
  infoLink?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
}

export const EmptyState: React.FC<EmptyStateProps> = (props) => {
  const {
    children,
    icon,
    iconShadow,
    header,
    content,
    headerColor = 'textPrimary',
    contentColor = 'textSecondary',
    variant = 'base',
    orientation = 'horizontal',
    gap,
    transparent = false,
    skipMargin = false,
    skipPadding = false,
    action,
    infoLink,
  } = props;
  const isSmall = variant === 'small';
  const headerFont = isSmall ? 'bold16' : 'bold18';
  const contentFont = 'regular14';
  const iconElement = cloneElement(icon, {
    ...icon.props,
    size: icon.props.size || (isSmall ? 40 : 80),
  });
  const iconShadowElement =
    iconShadow ?? cloneElement(icon, { ...icon.props, size: isSmall ? 120 : 240 });

  return (
    <Container
      orientation={orientation}
      $transparent={transparent}
      $skipMargin={skipMargin}
      $skipPadding={skipPadding}
      $variant={variant}
      $gap={gap}
    >
      <Card
        data-variant={variant}
        style={{
          '--icon-color': theme.color.elementPurpleInvert,
        }}
      >
        {iconElement}
        <Shadow
          style={{
            '--icon-color': theme.color.uiTrans5,
          }}
        >
          {iconShadowElement}
        </Shadow>
      </Card>
      <Stack space={4}>
        <TextContainer $orientation={orientation}>
          {header && <Text font={headerFont} color={headerColor} text={header} />}
          <Text font={contentFont} color={contentColor} text={content} />
          {infoLink && (
            <Link to={infoLink.href} style={{ marginTop: pxToRem(8) }}>
              <span onClick={infoLink.onClick} style={{ cursor: 'pointer' }}>
                <Text text={infoLink.label} underline font={contentFont} color={contentColor} />
              </span>
            </Link>
          )}
        </TextContainer>
        {action}
      </Stack>
      {children}
    </Container>
  );
};

type EmptyStateExtraSmallProps = {
  iconComponent?: (props: IconProps) => JSX.Element;
  title?: string;
  description?: string;
};

export const EmptyStateExtraSmall = ({
  iconComponent: Icon = IconSearch,
  title,
  description,
}: EmptyStateExtraSmallProps) => {
  return (
    <Stack
      orientation="horizontal"
      space={4}
      align="center"
      style={{
        padding: pxToRem(24),
        backgroundColor: theme.color.uiTrans5,
        borderRadius: pxToRem(8),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: pxToRem(12),
          borderRadius: pxToRem(8),
          backgroundColor: theme.color.backgroundLightDark,
        }}
      >
        <Icon color="elementPurpleInvert" size={24} />
      </div>
      <Stack space={2}>
        {title && <Text font="bold16" color="textPrimary" text={title} />}
        {description && <Text font="regular14" color="textSecondary" text={description} />}
      </Stack>
    </Stack>
  );
};

const Container = styled(Stack).attrs({ align: 'center' })<{
  $transparent: boolean;
  $skipMargin: boolean;
  $skipPadding: boolean;
  $variant: EmptyStateProps['variant'];
  $gap?: Spacing;
}>(({ $transparent, $skipMargin, $skipPadding, $variant, $gap }) => ({
  backgroundColor: $transparent ? 'transparent' : theme.color.uiTrans5,
  ...(!$skipMargin && {
    margin: pxToRem(24),
  }),
  ...(!$skipPadding && {
    padding: $variant === 'base' ? pxToRem(40) : `${pxToRem(16)} ${pxToRem(24)}`,
  }),
  borderRadius: pxToRem(8),
  gap: theme.space[$gap ?? ($variant === 'base' ? 10 : 8)],
}));

const TextContainer = styled.div<{ $orientation: 'horizontal' | 'vertical' }>(
  ({ $orientation }) => ({
    maxWidth: pxToRem(424),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.space[1],
    ...($orientation === 'vertical' && {
      textAlign: 'center',
    }),
  })
);

const Card = styled.div({
  backgroundColor: theme.color.backgroundLightDark,
  boxShadow: theme.shadow.fancy,
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: pxToRem(8),
  font: theme.font.yuge,
  flexShrink: 0,

  '&[data-variant="base"]': {
    width: '9.5rem',
    height: '9.5rem',
  },

  '&[data-variant="small"]': {
    width: '5rem',
    height: '5rem',
  },
});

const Shadow = styled.div({
  position: 'absolute',
  right: 0,
  bottom: 0,
});

export type EmptyStateCellProps = {
  icon: ReactElement<IconProps>;
  text: string;
  action?: ReactElement;
};

export const EmptyStateCell = ({ icon, text, action }: EmptyStateCellProps) => {
  const iconElement = cloneElement(icon, { color: 'elementPurple60', ...icon.props });

  return (
    <CellContainer>
      <Stack space={4} orientation="horizontal" align="center" justify="space-between">
        <Stack space={4} orientation="horizontal" align="center">
          {iconElement}
          <Text font="semibold14" text={text} color="textSecondary" />
        </Stack>
        {action}
      </Stack>
    </CellContainer>
  );
};

const CellContainer = styled.div({
  backgroundColor: theme.color.uiTrans5,
  borderRadius: pxToRem(8),
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
  padding: `${pxToRem(16)} ${pxToRem(24)}`,
  '> :first-child': {
    height: pxToRem(48),
  },
});
